import { useEffect, useState } from "react";
import { Box, CircularProgress} from "@mui/material";
import GenericTable, { Column } from "components/Data/DataDisplays/GenericTable";
import { Person, Address } from "api/Community/CommunityInterfaces";
import NoRecordsFound from "components/Records/NoRecordsFound";

/**
 * The generic log display component handles:
 * - Fetching logs via a passed-in API call
 * - Auto-flattening related data (people / addresses)
 * - Displaying them in a GenericTable
 */
export interface LogDisplayProps<T> {
  /** Function that fetches logs (e.g. getEmailLogs, getTextMessageLogs, etc.) */
  fetchLogs: () => Promise<T[] >;
  
  /** Table column definitions */
  columns: Column<any>[];

  /** Title above the table */
  title: string;

}

function LogDisplay<T>({
  fetchLogs,
  columns,
  title,
}: LogDisplayProps<T>) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 🔧 Generic data transformation — auto-flattens "people" or "addresses"
  const transformLogs = (logs: any[]): any[] => {
    return logs.map((log) => {
      if ("people" in log && Array.isArray(log.people) && log.people.length > 0) {
        const person: Person = log.people[0];
        return {
          ...log,
          firstName: person.first_name ?? "",
          lastName: person.last_name ?? "",
          phoneNumber: person.phone ?? "",
          email: person.email ?? "",
        };
      }

      if ("addresses" in log && Array.isArray(log.addresses) && log.addresses.length > 0) {
        const address: Address = log.addresses[0];
        return {
          ...log,
          address: address.street_address ?? "",
          city: address.city ?? "",
          country: address.country ?? "",
        };
      }

      return log;
    });
  };

  useEffect(() => {
    const loadLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const logs = await fetchLogs();
        const transformed = transformLogs(logs);
        setData(transformed);
      } catch (err) {
        console.error(err);
        setError("Failed to load logs.");
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, [fetchLogs]);

  return (
    <Box sx={{height: "100%", overflow: "auto" }}>
      <GenericTable<any>
        data={data}
        columns={columns}
        loading={loading}
        error = {error}
        title={title}
        getRowLink={(row) => row.people?.[0]?.id ? `/community/people/${row.people[0].id}` : undefined}
      />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}

export default LogDisplay;
